import type { Design } from '@ff-image-frame-engine/_types/Design';
import { DesignOverrides } from '@ff-image-frame-engine/_types/DesignOverrides';
import { readDesignFile } from '@ff-image-frame-engine/readDesignFile';
import { urls } from '@ff-os/shared-utils/urls';
import { actions as photosActions } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpDispatch } from '@ff-scene-plugins/photo-prints-redux/store';
import { Paintable, PipelinePlugin, PipelineState } from 'ff-canvas';
import { getMagicFrameEngine } from './magicFrameEngineManager';

const createMagicBorderPlugin = (dispatch: PhotoPrintsPdpDispatch): PipelinePlugin => {

    interface PhotoCacheEntry {
        canvas: HTMLCanvasElement;
        magicBorderAssetId: string;
    }

    const magicFrameDesigns: {[assetId: string]: Design} = {};
    const photoCache: {[cacheKey: string]: PhotoCacheEntry} = {}; // TODO Clean removed photos

    return {

        preload: async (objects: Paintable[], state: PipelineState): Promise<void> => {

            for (const object of objects.filter(o => o.type === 'photo' && o.custom?.values?.magicBorderAssetId)) {
                const assetId = object.custom.values.magicBorderAssetId!;
                const url = urls.publicAssetUrl(assetId);
                const request = await fetch(url);
                if (!request.ok) {
                    throw new Error(`Failed to fetch magic border asset with ID "${assetId}" from URL "${url}"`);
                }
                const blobData = await request.blob();
                magicFrameDesigns[assetId] = await readDesignFile(blobData);
            }
        },

        onAfterPaintAll: (state: PipelineState, objects: Paintable[]) => {

            const draw = (frame: HTMLCanvasElement, variantScale: number) => {

                if (!(state.canvas instanceof HTMLCanvasElement)) {
                    throw new Error('Canvas must be an HTMLCanvasElement for magic border rendering');
                }

                const excessWidth = state.canvas.width * (variantScale - 1);
                const excessHeight = state.canvas.height * (variantScale - 1);

                const x = Math.floor(-excessWidth / 2);
                const y = Math.floor(-excessHeight / 2);

                const width = Math.ceil(state.canvas.width * variantScale);
                const height = Math.ceil(state.canvas.height * variantScale);

                const dpr = window.devicePixelRatio || 1;
                const ctx = state.canvas.getContext('2d')!;
                ctx.save();
                ctx.globalCompositeOperation = 'destination-over';
                ctx.imageSmoothingEnabled = false;
                ctx.drawImage(frame, x / dpr, y / dpr, width / dpr, height / dpr);
                ctx.restore();
            };

            for (const object of objects
                .filter(o => o.type === 'photo'
                    && o.custom?.values?.magicBorderAssetId)) {

                const assetId = object.custom.values.magicBorderAssetId!;
                const asset = magicFrameDesigns[assetId];
                const variantScale = object.custom.values.patternScale!;
                if (!asset) {
                    throw new Error(`Magic border asset with ID "${assetId}" not preloaded`);
                }

                if (object.type !== 'photo') {
                    throw new Error('Magic border plugin can only be applied to Photo objects');
                }

                const cacheKey = [
                    object.id,
                    object.width,
                    object.height,
                    object.innerZoom,
                    object.innerOffsetX,
                    object.innerOffsetY,
                    object.strokeSize
                ].join(':');

                const cacheEntry = photoCache[cacheKey];
                if (cacheEntry && cacheEntry.magicBorderAssetId === assetId) {
                    draw(cacheEntry.canvas, variantScale);
                    continue;
                }

                const engine = getMagicFrameEngine();

                const magicFrameScene = {
                    width: Math.floor(state.canvas.width as number * variantScale),
                    height: Math.floor(state.canvas.height as number * variantScale),
                    objects: [{
                        x: 0,
                        y: 0,
                        width: Math.ceil(state.canvas.width as number * variantScale),
                        height: Math.ceil(state.canvas.height as number * variantScale),
                        image: state.canvas,
                        key: cacheKey
                    }]
                };

                const isPortrait = state.canvas.width <= state.canvas.height;

                let overrides: DesignOverrides = (
                    isPortrait
                        ? object.custom?.values?.designOverridePortrait
                        : object.custom?.values?.designOverrideLandscape
                ) || {};

                // only paint and blob have more than 1 image, so we don't need to override the design
                if (asset.images.length > 1) {
                    overrides = {};
                }

                const reproString = object.custom?.values?.reproString
                    ? engine.renderFromReproducibleFrame(
                        magicFrameScene, asset,
                        object.custom.values.reproString, overrides)
                    : engine.render(
                        magicFrameScene, asset,
                        object.custom?.values?.magicBorderRngSeed,
                        overrides);

                const frameCanvas = engine.getCanvas();
                const cacheCanvas = document.createElement('canvas');

                cacheCanvas.width = frameCanvas.width;
                cacheCanvas.height = frameCanvas.height;
                const cacheCtx = cacheCanvas.getContext('2d')!;
                cacheCtx.drawImage(frameCanvas, 0, 0, frameCanvas.width, frameCanvas.height);

                photoCache[cacheKey] = {
                    canvas: cacheCanvas,
                    magicBorderAssetId: assetId
                };

                draw(frameCanvas, variantScale);

                if (!object.custom?.values?.reproString) {
                    dispatch(photosActions.saveReproString({
                        photoId: object.id,
                        reproString: reproString
                    }));
                }
            }
        },

        onAfterPaint: (state: PipelineState, object: Paintable): void => {},
        onBeforePaint: (state: PipelineState, object: Paintable): void => {},
        onBeforePaintAll: (state: PipelineState, objects: Paintable[]): void => {}
    };
};

export { createMagicBorderPlugin };